import React, { useReducer } from 'react';
import { parseURLQueryParams } from '@askporter/component-library/parseURLQueryParams';
import { Table, CustomTableCell } from '@askporter/component-library/Table';
import { URLQueryParams } from '@askporter/component-library/URLQueryParams';
import { UserAvatar } from '@askporter/component-library/UserAvatar';
import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@askporter/auth-provider';
import { ExportsListExternal, RoleSlug } from '@askporter/client-grieg-lyric';
import { useTheme } from '@askporter/component-library';
import type { TFunction } from '@askporter/config-provider';
import { queryKeys } from '@askporter/query-keys';
import { formatDate } from '@askporter/utils';
import { ExportStatus } from '../../../types';
import { ExportDownloadButton } from '../../ExportDownloadButton';
import fetchExports from './fetchExports';
import reducer, { ExportListState, initialState } from './reducer';
import { ExportNameField } from './TableBodyContent/ExportNameField';
import { ExportStatusChip } from './TableBodyContent/ExportStatusChip';
import { ExportViewButton } from './TableBodyContent/ExportViewButton';
interface ExportListProps {
  t: TFunction;
}
/**
 * The ExportList component is the main component for the Export List page.
 */
export const ExportList: React.FC<ExportListProps> = ({ t }) => {
  const {
    custom: { spacing },
  } = useTheme();
  const isUserAdmin = usePermissions([RoleSlug.ClientAdmin, RoleSlug.PlatAdmin]);
  const [state, dispatch] = useReducer(reducer, initialState(), (state): ExportListState => {
    // Parse the page_token from the URL query params
    const { page_token } = parseURLQueryParams<Pick<ExportListState, 'page_token'>>(Object.keys(state));
    return { ...state, page_token: JSON.stringify(page_token) };
  });
  const { data, isPending } = useQuery<ExportsListExternal>({
    queryKey: queryKeys.exports.list.page(state?.page_token),
    queryFn: fetchExports,
  });
  // Define header rows based on user permissions
  const headerRows = [
    {
      cells: [
        t('export:export_list:table:exports_header'),
        t('export:export_list:table:results_header'),
        t('export:export_list:table:status_header'),
        ...(isUserAdmin ? [t('export:export_list:table:executed_by_header')] : []),
        t('export:export_list:table:date_header'),
        '', // View button column
        '', // Download button column
      ],
      type: 'data' as const,
    },
  ];
  // Custom render function for table rows
  const renderRow = (exportItem: any) => {
    const {
      uid,
      name,
      type,
      resultCount,
      progress: { status },
      createdBy,
      requestedAt,
    } = exportItem;
    return (
      <>
        <CustomTableCell>
          <ExportNameField name={name} type={type} t={t} />
        </CustomTableCell>
        <CustomTableCell>{resultCount}</CustomTableCell>
        <CustomTableCell>
          <ExportStatusChip status={status} t={t} />
        </CustomTableCell>
        {isUserAdmin && (
          <CustomTableCell>
            <UserAvatar
              givenName={createdBy?.givenName}
              familyName={createdBy?.familyName}
              imagePath={createdBy.profilePhoto?.filePath}
              mode="full"
              textMode="label"
            />
          </CustomTableCell>
        )}
        <CustomTableCell>{formatDate(requestedAt, 'dd MMM yy HH:mm')}</CustomTableCell>
        <CustomTableCell>
          <ExportViewButton t={t} uid={uid} />
        </CustomTableCell>
        <CustomTableCell>
          {(status as ExportStatus) === 'COMPLETED' && (
            <ExportDownloadButton
              uid={uid}
              label={t('export:export_list:table:download_button')}
              onClick={() => ({})}
            />
          )}
        </CustomTableCell>
      </>
    );
  };
  // Pagination configuration
  const pagination = {
    showPagination: true,
    currentPage: state.previousPageTokens.length + 1,
    disableNext: !data?.page?.nextPageToken,
    disablePrevious: state.previousPageTokens.length === 0,
    onPageChange: (page: number) => {
      const currentPage = state.previousPageTokens.length + 1;
      if (page > currentPage && data?.page?.nextPageToken) {
        dispatch({ type: 'HANDLE_NEXT_BUTTON_CLICK', payload: data.page.nextPageToken });
      } else if (page < currentPage && state.previousPageTokens.length > 0) {
        dispatch({ type: 'HANDLE_PREVIOUS_BUTTON_CLICK' });
      }
    },
  };
  return (
    <>
      <URLQueryParams
        // only page_token is saved in the URL query params
        // if page_token is undefined, pass empty string in so it gets cleared from the URL
        state={{ page_token: state?.page_token || '' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: spacing['5xl'],
        }}
      >
        <Table
          data={data?.exports || []}
          renderRow={renderRow}
          headerRows={headerRows}
          isLoading={isPending}
          loadingRowsCount={5}
          customCellStyles={{ whiteSpace: 'nowrap' }}
          pagination={pagination}
          empty={<Box sx={{ p: 3, textAlign: 'center' }}>{t('export:export_list:table:no_exports')}</Box>}
        />
      </Box>
    </>
  );
};
